import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

interface City {
  id: string;
  name_en: string;
  name_ar: string | null;
  region: string | null;
  is_active: boolean;
}

interface CityPickerProps {
  value: string | null | undefined;
  onChange: (cityName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Searchable city dropdown backed by /api/v1/cities (PR MAY10 P4A.6).
 * When user picks "Other (type manually)", an inline text input appears.
 * On submit, the new city auto-inserts to the cities table via POST so it
 * appears in future dropdowns. value is the city name string (not id).
 */
export function CityPicker({ value, onChange, placeholder, disabled, required }: CityPickerProps) {
  const { t } = useTranslation();
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [showOther, setShowOther] = useState(false);
  const [otherName, setOtherName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiClient.get<{ items: City[] }>('/api/v1/cities')
      .then((r) => { if (!cancelled) setCities(r.data.items); })
      .catch((e) => { if (!cancelled) setError(extractErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter(c =>
      c.name_en.toLowerCase().includes(q) ||
      (c.name_ar ?? '').toLowerCase().includes(q)
    );
  }, [cities, filter]);

  async function submitOther() {
    const name = otherName.trim();
    if (name.length < 2) return;
    setSubmitting(true);
    try {
      const r = await apiClient.post<{ city: City }>('/api/v1/cities', { nameEn: name });
      setCities(prev => {
        if (prev.some(c => c.id === r.data.city.id)) return prev;
        return [...prev, r.data.city].sort((a, b) => a.name_en.localeCompare(b.name_en));
      });
      onChange(r.data.city.name_en);
      setShowOther(false);
      setOtherName('');
    } catch (e) {
      setError(extractErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="text-xs text-muted-foreground">{t('common.loading', { defaultValue: 'Loading…' })}</div>;
  }
  if (error) {
    return <div className="text-xs text-destructive">{error}</div>;
  }

  return (
    <div className="space-y-2">
      <select
        value={showOther ? '__other__' : (value ?? '')}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '__other__') { setShowOther(true); return; }
          setShowOther(false);
          onChange(v);
        }}
        disabled={disabled}
        required={required}
        className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base"
      >
        <option value="">{placeholder ?? t('city.placeholder', { defaultValue: 'Select city…' })}</option>
        {filtered.map(c => (
          <option key={c.id} value={c.name_en}>
            {c.name_en}{c.name_ar ? ` · ${c.name_ar}` : ''}{c.region ? ` (${c.region})` : ''}
          </option>
        ))}
        <option value="__other__">{t('city.other', { defaultValue: 'Other (type manually)' })}</option>
      </select>

      {cities.length > 10 && !showOther && (
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('city.search', { defaultValue: 'Search cities…' })}
          disabled={disabled}
          className="h-9"
        />
      )}

      {showOther && (
        <div className="rounded-md border border-dashed border-input p-3 space-y-2 bg-muted/20">
          <Input
            value={otherName}
            onChange={(e) => setOtherName(e.target.value)}
            placeholder={t('city.otherPlaceholder', { defaultValue: 'Type city name (e.g. "Al Hofuf")' })}
            disabled={submitting}
            autoFocus
          />
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" size="sm" onClick={() => { setShowOther(false); setOtherName(''); }} disabled={submitting}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type="button" size="sm" onClick={submitOther} disabled={submitting || otherName.trim().length < 2}>
              {submitting ? t('common.saving', { defaultValue: 'Saving…' }) : t('city.addAndSelect', { defaultValue: 'Add & select' })}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
